/**
 * React Query hooks for the gocore-api MightyKiwi odds endpoint:
 *   GET /api/MightyKiwi/odds            -> Counter-Strike + DOTA 2 fixtures
 *   GET /api/MightyKiwi/odds?game=cs2   -> single title
 *
 * Hand-written to match the orval/react-query + customInstance convention used by
 * the generated client (query functions resolve to the customFetch wrapper, so
 * consumers read `query.data?.data`). Replace with generated hooks once the BE
 * endpoint is in swagger and `pnpm run generate:api` is run.
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { env } from '@/config/env';
import customFetch from '@/lib/customInstance';
import type {
  EsportOddsGameFilter,
  MightyKiwiEsportOdds,
} from './mightyKiwi.types';

const BASE = `${env.NEXT_PUBLIC_API_URL}/api/MightyKiwi`;

type FetchWrapper<T> = { status: number; data: T; headers: Headers };

export type GetEsportOddsParams = {
  /** Optional title filter; omit for both CS2 and DOTA 2. */
  game?: EsportOddsGameFilter;
};

export const getEsportOdds = (params?: GetEsportOddsParams, signal?: AbortSignal) =>
  customFetch<MightyKiwiEsportOdds[]>({
    url: `${BASE}/odds`,
    method: 'GET',
    params,
    signal,
  });

export const mightyKiwiKeys = {
  odds: (params?: GetEsportOddsParams) => ['mighty-kiwi', 'odds', params ?? {}] as const,
};

export const useGetEsportOdds = (
  params?: GetEsportOddsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        FetchWrapper<MightyKiwiEsportOdds[]>,
        unknown,
        FetchWrapper<MightyKiwiEsportOdds[]>
      >
    >;
  }
) =>
  useQuery({
    queryKey: mightyKiwiKeys.odds(params),
    queryFn: ({ signal }) => getEsportOdds(params, signal),
    ...options?.query,
  });
